import { TooltipButton } from 'components';
import { useTranslation } from 'react-i18next';
import { FilePdfOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { selectors } from 'app/services/sites';
import { selectors as sessionSelectors } from 'app/services/session';
import { useLazyGetSiteToolsQuery } from 'app/services/tools';
import { useGetUnitQuery } from 'app/services/me';
import { RootState } from 'app/store';
import { UnitType } from 'constants/index';
import { generateToolReportPdf } from 'utils/toolReport';
import { showError, showWarning } from 'utils/notifications';

type ToolReportProps = {
  isExecuting?: (isFetching: boolean) => void,
}

const ToolReport = ({ isExecuting }: ToolReportProps) => {
  const { t } = useTranslation();
  const site = useSelector(selectors.getSite);
  const isClientUnit = useSelector(state => sessionSelectors.hasUnitType(state as RootState, UnitType.CLIENT));
  const { data: unit } = useGetUnitQuery({}, { skip: !isClientUnit });
  const [ getSiteTools ] = useLazyGetSiteToolsQuery();
  const [ isGenerating, setIsGenerating ] = useState(false);

  useEffect(() => {
    if (isExecuting) isExecuting(isGenerating);
  }, [ isGenerating, isExecuting ]);

  if (!site || !isClientUnit) return null;

  const handleClick = async () => {
    setIsGenerating(true);

    try {
      const tools = await getSiteTools(site.Id).unwrap();
      if (!tools.length) {
        showWarning(t('pages.tools.report.notools'));
        return;
      }

      const pdf = await generateToolReportPdf(tools, unit?.Name, t, site.FullAddress);
      pdf.save(`kvalilog-tools-report-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (err: any) {
      // API errors carry a status and are already shown by exceptionsMiddleware
      if (!err?.status) showError(t('pages.tools.report.failed'), err?.message);
    } finally {
      setIsGenerating(false);
    }
  };

  return <TooltipButton
    title={t('pages.tools.report.title')}
    buttonProps={{
      onClick: handleClick,
      icon: <FilePdfOutlined />,
      type: 'link',
      loading: isGenerating,
    }}
  />;
};

export default ToolReport;
